import React, { useCallback, useEffect, useState } from "react";
import { useSettingsStore } from "../../../../Store/Settings";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AutoCollectWaitOptions,
  DelayInvoiceEmailOptions,
  Meridiem,
  SettingsPatch
} from "../../../../Contracts/Settings";
import { ReactComponent as InfoIcon } from "../../../../Assets/ActionIcons/InfoIcon.svg";
import { ReactComponent as QuestionIcon } from "../../../../Assets/ActionIcons/QuestionIcon.svg";
import DaySelectOptions from "../DaySelectOptions";
import Toggle from "../../../../Components/Atoms/Toggle";
import { useTeamStore } from "../../../../Store/Team";
import { useOnboardingStore } from "../../../../Store/Onboarding";
import { PaymentsIntegrationEnum } from "../../../../Contracts/Integration";
import Modal from "../../../../Components/Atoms/Modal";
import { capitalizeAfterUnderscore } from "../../../../Contracts/Common";

const ConfigureInvoicePreferenceTab: React.FC = () => {
  const { isLoading, settings, getSettings, updateSettings } = useSettingsStore();
  const { adyenKYCDetails } = useOnboardingStore();
  const { currentTeam, getCurrentTeam, getCurrencySymbol } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();
  const [dirtySettings, setDirtySettings] = useState<SettingsPatch>({});
  const [
    customFieldFilterAutoCollectSwitchConfirmModalOpen,
    setCustomFieldFilterAutoCollectSwitchConfirmModalOpen
  ] = useState(false);
  const [disableAndDeleteConfirmationModalOpen, setDisableAndDeleteConfirmationModalOpen] =
    useState(false);

  const [customFieldToBeSelectedError, setCustomFieldToBeSelectedError] = useState(false);
  const refreshThings = useCallback(async () => {
    await getSettings(getAccessTokenSilently);
  }, [getAccessTokenSilently, getSettings]);
  useEffect(() => {
    getCurrentTeam(getAccessTokenSilently);
    refreshThings();
  }, [refreshThings, getAccessTokenSilently, getCurrentTeam]);
  const updatedSettings = { ...settings, ...dirtySettings };

  const saveAndContinue = async () => {
    if (validateCustomField()) return;
    if (Object.keys(dirtySettings).length === 0) return;
    let dirtySettingsToPatch = dirtySettings;
    if (dirtySettings.payment_custom_field_filter_enabled) {
      dirtySettingsToPatch = {
        payment_default_auto_collect_based_on_custom_field:
          updatedSettings.payment_default_auto_collect_based_on_custom_field === undefined
            ? false
            : updatedSettings.payment_default_auto_collect_based_on_custom_field,
        ...dirtySettingsToPatch
      };
    }
    const success = await updateSettings(getAccessTokenSilently, dirtySettingsToPatch);
    if (!success) {
      setDirtySettings({});
      return;
    }
    setDirtySettings({});
  };
  const isDirty = dirtySettings ? Object.keys(dirtySettings).length > 0 : false;

  const delayEmailsDays = updatedSettings.delay_invoice_email_seconds_after_creation
    ? Math.floor(updatedSettings.delay_invoice_email_seconds_after_creation / 86400)
    : 0;
  const delayEmailsHours = updatedSettings.delay_invoice_email_seconds_after_creation
    ? Math.floor((updatedSettings.delay_invoice_email_seconds_after_creation % 86400) / 3600)
    : 0;
  const delayEmailsMinutes = updatedSettings.delay_invoice_email_seconds_after_creation
    ? Math.floor((updatedSettings.delay_invoice_email_seconds_after_creation % 3600) / 60)
    : 5;

  const timeLessThanFiveMinutes =
    delayEmailsDays === 0 && delayEmailsHours === 0 && delayEmailsMinutes < 5;

  const userHasChangedDelayTime =
    updatedSettings.delay_invoice_email_option === DelayInvoiceEmailOptions.ScheduleAfter
      ? dirtySettings.delay_invoice_email_seconds_after_creation !== undefined
      : dirtySettings.delay_invoice_email_every_day_at !== undefined;

  const validateCustomField = (): boolean => {
    if (
      updatedSettings.payment_custom_field_filter_enabled &&
      !updatedSettings.selected_custom_field
    ) {
      setCustomFieldToBeSelectedError(true);
      return true;
    } else {
      setCustomFieldToBeSelectedError(false);
      return false;
    }
  };

  return (
    <>
      <div
        className={`relative flex flex-col h-[calc(100vh-16rem)] overflow-y-auto ${
          isLoading ? "cursor-progress" : ""
        }`}
      >
        <div className="md:w-2/4 h-full">
          <div className="ml-8">
            {adyenKYCDetails?.connected_integration === PaymentsIntegrationEnum.Quickbooks && (
              <div className="my-8">
                <Modal
                  isOpen={customFieldFilterAutoCollectSwitchConfirmModalOpen}
                  onClose={() => setCustomFieldFilterAutoCollectSwitchConfirmModalOpen(false)}
                  title={
                    <div className="flex space-x-2 font-semibold text-lg mb-4">
                      <QuestionIcon className="text-orange-500" />
                      <div>Are you sure?</div>
                    </div>
                  }
                  okText="Yes"
                  closable={false}
                  topOffset="10rem"
                  onOk={() => {
                    setCustomFieldFilterAutoCollectSwitchConfirmModalOpen(false);
                    setDirtySettings({
                      ...dirtySettings,
                      payment_default_auto_collect_based_on_custom_field:
                        updatedSettings.payment_default_auto_collect_based_on_custom_field ===
                        undefined
                          ? false
                          : !updatedSettings.payment_default_auto_collect_based_on_custom_field
                    });
                  }}
                  onCancel={() => setCustomFieldFilterAutoCollectSwitchConfirmModalOpen(false)}
                >
                  <div>
                    Changing the global settings will also reverse the auto-collect behaviour for
                    client level invoices.
                  </div>
                </Modal>
                <Modal
                  isOpen={disableAndDeleteConfirmationModalOpen}
                  onClose={() => setDisableAndDeleteConfirmationModalOpen(false)}
                  title={
                    <div className="flex space-x-2 font-semibold text-lg mb-4">
                      <QuestionIcon className="text-orange-500" />
                      <div>Are you sure you want to proceed?</div>
                    </div>
                  }
                  topOffset="10rem"
                  onOk={() => {
                    dirtySettings.payment_custom_field_disable_and_delete === undefined &&
                      setDirtySettings({
                        ...dirtySettings,
                        payment_custom_field_disable_and_delete: false
                      });
                    setDisableAndDeleteConfirmationModalOpen(false);
                  }}
                  onCancel={() => setDisableAndDeleteConfirmationModalOpen(false)}
                >
                  <div className="my-2">
                    You are about to disable/ delete all created Custom Field Filters.
                  </div>
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      setDirtySettings({
                        ...dirtySettings,
                        payment_custom_field_disable_and_delete: false
                      })
                    }
                  >
                    <input
                      type="radio"
                      className="mr-2"
                      checked={!dirtySettings.payment_custom_field_disable_and_delete}
                    />
                    <label>Only disable custom field filters</label>
                  </div>
                  <div
                    onClick={() =>
                      setDirtySettings({
                        ...dirtySettings,
                        payment_custom_field_disable_and_delete: true
                      })
                    }
                    className="cursor-pointer"
                  >
                    <input
                      type="radio"
                      className="mr-2"
                      checked={dirtySettings.payment_custom_field_disable_and_delete}
                    />
                    <label>Disable and delete all custom field filters</label>
                  </div>
                </Modal>
                <div className="flex items-center space-x-4 my-2">
                  <label className="font-semibold">Custom Field Filters</label>
                  <Toggle
                    checked={updatedSettings.payment_custom_field_filter_enabled || false}
                    isLoading={isLoading}
                    onChange={enabled => {
                      !enabled &&
                        settings?.payment_custom_field_filter_enabled &&
                        setDisableAndDeleteConfirmationModalOpen(true);
                      setDirtySettings({
                        ...dirtySettings,
                        payment_custom_field_filter_enabled: enabled
                      });
                    }}
                  />
                </div>
                <span className="text-gray-500">
                  Zomentum Payments can match a value of a custom field in your invoice with your
                  pre-configured values to decide if it should be auto-collected or not.
                </span>
                <div
                  className={`bg-white-smoke p-4 rounded mt-4 ${
                    updatedSettings.payment_custom_field_filter_enabled
                      ? ""
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  <div className="my-2">
                    Select the custom field to use for auto-collect
                    <select
                      value={updatedSettings.selected_custom_field || ""}
                      className={`px-3 py-1 border border-gray-5 rounded w-30 mx-2 ${
                        updatedSettings.payment_custom_field_filter_enabled
                          ? ""
                          : "opacity-50 cursor-not-allowed"
                      }`}
                      disabled={!updatedSettings.payment_custom_field_filter_enabled}
                      onChange={e =>
                        setDirtySettings({
                          ...dirtySettings,
                          selected_custom_field: e.target.value
                        })
                      }
                    >
                      <option value="" disabled>
                        Select a custom field
                      </option>
                      {updatedSettings.available_custom_fields?.map((field, index) => (
                        <option key={index} value={field}>
                          {capitalizeAfterUnderscore(field.replace("custom::", ""))}
                        </option>
                      )) || []}
                    </select>
                    {customFieldToBeSelectedError && (
                      <span className="text-red-600">Please select a custom field</span>
                    )}
                  </div>

                  <div className="my-4">
                    Select the default behavior for invoices that don't have a matching value for
                    the custom field
                  </div>
                  <div className="flex items-center space-x-2">
                    <div
                      className="flex items-center space-x-2"
                      onClick={() =>
                        updatedSettings.payment_custom_field_filter_enabled &&
                        updatedSettings.payment_default_auto_collect_based_on_custom_field !==
                          true &&
                        setCustomFieldFilterAutoCollectSwitchConfirmModalOpen(true)
                      }
                    >
                      <input
                        type="radio"
                        disabled={!updatedSettings.payment_custom_field_filter_enabled}
                        checked={
                          updatedSettings.payment_default_auto_collect_based_on_custom_field ===
                          undefined
                            ? false
                            : updatedSettings.payment_default_auto_collect_based_on_custom_field
                        }
                        className={
                          updatedSettings.payment_custom_field_filter_enabled
                            ? ""
                            : "cursor-not-allowed"
                        }
                      />

                      <label>Auto-collect enabled</label>
                    </div>
                    <div
                      className="flex items-center space-x-2"
                      onClick={() =>
                        updatedSettings.payment_custom_field_filter_enabled &&
                        updatedSettings.payment_default_auto_collect_based_on_custom_field ===
                          true &&
                        setCustomFieldFilterAutoCollectSwitchConfirmModalOpen(true)
                      }
                    >
                      <input
                        type="radio"
                        disabled={!updatedSettings.payment_custom_field_filter_enabled}
                        checked={
                          updatedSettings.payment_default_auto_collect_based_on_custom_field ===
                          undefined
                            ? true
                            : !updatedSettings.payment_default_auto_collect_based_on_custom_field
                        }
                        className={
                          updatedSettings.payment_custom_field_filter_enabled
                            ? ""
                            : "cursor-not-allowed"
                        }
                      />
                      <label>Auto-collect disabled</label>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="my-8">
              <div className="flex items-center space-x-4 my-2">
                <label className="font-semibold">Auto Collect Limit</label>
                <Toggle
                  checked={updatedSettings.payment_auto_collect_limit_enabled || false}
                  isLoading={isLoading}
                  onChange={enabled =>
                    setDirtySettings({
                      ...dirtySettings,
                      payment_auto_collect_limit_enabled: enabled
                    })
                  }
                />
              </div>
              <span className="text-gray-500">
                Any invoice with a value higher than this amount will not be automatically
                collected.
              </span>
              <div className="flex my-2">
                <div className="rounded-l pl-1 py-1 border-y border-l border-gray-300">
                  {getCurrencySymbol()}
                </div>
                <input
                  className="max-w-32 px-3 py-1 border-y border-gray-300"
                  type="number"
                  min={0}
                  step="any"
                  disabled={!updatedSettings.payment_auto_collect_limit_enabled}
                  value={updatedSettings.payment_auto_collect_limit ?? 0}
                  onChange={e =>
                    setDirtySettings({
                      ...dirtySettings,
                      payment_auto_collect_limit: parseFloat(e.target.value)
                    })
                  }
                />
                <div className="rounded-r px-2 py-1 border border-gray-300 bg-gray-100 text-gray-600 mr-2">
                  {currentTeam?.currency || "USD"}
                </div>
              </div>
            </div>

            <div className="my-8">
              <div className="flex items-center space-x-4 my-2">
                <label className="font-semibold">Delay Invoice Email</label>
                <Toggle
                  checked={updatedSettings.delay_invoice_email_enabled || false}
                  isLoading={isLoading}
                  onChange={enabled =>
                    setDirtySettings({
                      ...dirtySettings,
                      delay_invoice_email_enabled: enabled
                    })
                  }
                />
                <div className="font-medium flex items-center space-x-2 bg-gray-100 px-2 py-1 rounded">
                  <InfoIcon className="w-4 h-4" />
                  <div>All times are in UTC</div>
                </div>
              </div>
              <span className="text-gray-500">
                Configure a time delay for sending automatic invoice emails. This is useful if you
                need to make changes to invoices after they have been created but before Zomentum
                processes them.
              </span>
              <div
                className={`bg-white-smoke p-4 rounded mt-4 ${
                  updatedSettings.delay_invoice_email_enabled ? "" : "opacity-50 cursor-not-allowed"
                }`}
              >
                <div className="flex items-center space-x-2 mb-4">
                  <div
                    className="flex items-center space-x-2"
                    onClick={() =>
                      updatedSettings.delay_invoice_email_enabled &&
                      setDirtySettings({
                        ...dirtySettings,
                        delay_invoice_email_option: DelayInvoiceEmailOptions.ScheduleAfter
                      })
                    }
                  >
                    <input
                      type="radio"
                      checked={
                        updatedSettings.delay_invoice_email_option ===
                        DelayInvoiceEmailOptions.ScheduleAfter
                      }
                      disabled={!updatedSettings.delay_invoice_email_enabled}
                    />
                    <label
                      className={
                        updatedSettings.delay_invoice_email_enabled
                          ? "cursor-pointer"
                          : "cursor-not-allowed"
                      }
                    >
                      Schedule After
                    </label>
                  </div>
                  <div
                    className="flex items-center space-x-2"
                    onClick={() =>
                      updatedSettings.delay_invoice_email_enabled &&
                      setDirtySettings({
                        ...dirtySettings,
                        delay_invoice_email_option: DelayInvoiceEmailOptions.EveryDay
                      })
                    }
                  >
                    <input
                      type="radio"
                      checked={
                        updatedSettings.delay_invoice_email_option ===
                        DelayInvoiceEmailOptions.EveryDay
                      }
                      disabled={!updatedSettings.delay_invoice_email_enabled}
                    />
                    <label
                      className={`${
                        updatedSettings.delay_invoice_email_enabled
                          ? "cursor-pointer"
                          : "cursor-not-allowed"
                      }`}
                    >
                      Every Day
                    </label>
                  </div>
                </div>
                {updatedSettings.delay_invoice_email_option ===
                DelayInvoiceEmailOptions.ScheduleAfter ? (
                  <div>
                    <div className="mt-4 flex items-center space-x-2 flex-wrap">
                      <span>Send invoice email</span>
                      <div className="flex items-center">
                        <input
                          className="max-w-20 px-3 py-1 border-y border-l  rounded-l border-gray-300"
                          type="number"
                          disabled={!updatedSettings.delay_invoice_email_enabled}
                          value={delayEmailsDays}
                          onChange={e =>
                            setDirtySettings({
                              ...dirtySettings,
                              delay_invoice_email_seconds_after_creation:
                                parseInt(e.target.value) * 86400 +
                                delayEmailsHours * 3600 +
                                delayEmailsMinutes * 60
                            })
                          }
                        />
                        <div className="rounded-r px-2 py-1 border border-gray-300 bg-gray-100 text-gray-600 mr-2">
                          days
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          className="max-w-20 px-3 py-1 border-y border-l  rounded-l border-gray-300"
                          type="number"
                          disabled={!updatedSettings.delay_invoice_email_enabled}
                          value={delayEmailsHours}
                          onChange={e =>
                            setDirtySettings({
                              ...dirtySettings,
                              delay_invoice_email_seconds_after_creation:
                                delayEmailsDays * 86400 +
                                parseInt(e.target.value) * 3600 +
                                delayEmailsMinutes * 60
                            })
                          }
                        />
                        <div className="rounded-r px-2 py-1 border border-gray-300 bg-gray-100 text-gray-600 mr-2">
                          hrs
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          className="max-w-20 px-3 py-1 border-y border-l  rounded-l border-gray-300"
                          type="number"
                          disabled={!updatedSettings.delay_invoice_email_enabled}
                          value={delayEmailsMinutes}
                          onChange={e =>
                            setDirtySettings({
                              ...dirtySettings,
                              delay_invoice_email_seconds_after_creation:
                                delayEmailsDays * 86400 +
                                delayEmailsHours * 3600 +
                                parseInt(e.target.value) * 60
                            })
                          }
                        />
                        <div className="rounded-r px-2 py-1 border border-gray-300 bg-gray-100 text-gray-600 mr-2">
                          mins
                        </div>
                      </div>
                      <span>after creating invoice.</span>
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <InfoIcon className="w-4 h-4" />
                      <div>The invoice email delay must be at least 5 minutes.</div>
                    </div>
                    {timeLessThanFiveMinutes && userHasChangedDelayTime && (
                      <div className="mt-2 text-red-600">
                        The email delay must be at least 5 minutes.
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      value={updatedSettings.delay_invoice_email_every_day_at || 0}
                      type="number"
                      className="px-3 py-1 border border-gray-5 rounded w-20 mx-2"
                      disabled={!updatedSettings.delay_invoice_email_enabled}
                      onChange={e =>
                        setDirtySettings({
                          ...dirtySettings,
                          delay_invoice_email_every_day_at: parseInt(e.target.value)
                        })
                      }
                      min={1}
                      max={12}
                    />
                    <select
                      disabled={!updatedSettings.delay_invoice_email_enabled}
                      className="px-3 py-1 border border-gray-5 rounded w-20 mx-2"
                      value={updatedSettings.delay_invoice_email_meridiem || Meridiem.AM}
                      onChange={e =>
                        setDirtySettings({
                          ...dirtySettings,
                          delay_invoice_email_meridiem: e.target.value as Meridiem
                        })
                      }
                    >
                      <option value={Meridiem.AM}>AM</option>
                      <option value={Meridiem.PM}>PM</option>
                    </select>
                  </div>
                )}

                {userHasChangedDelayTime && (
                  <div className="flex items-center my-2 space-x-2 p-3 bg-amber-100 rounded text-orange-700 font-semibold">
                    <InfoIcon className="w-4 h-4" />
                    <div>
                      Changed invoice email delay settings are only applicable to future invoices
                      and new email schedules.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="my-8">
              <span className="my-2 font-semibold">Auto Collect schedule</span>
              <div className="my-2">
                <span>Schedule auto collect after</span>
                <select
                  className="px-3 mr-2 py-1 border border-gray-5 rounded w-30 mx-2"
                  value={updatedSettings.payment_wait_days_until_due_date}
                  onChange={e => {
                    setDirtySettings({
                      ...dirtySettings,
                      payment_wait_days_until_due_date: parseInt(e.target.value)
                    });
                  }}
                >
                  <DaySelectOptions />
                </select>
                <span>or on the invoice due date, whichever is</span>
                <select
                  className="px-3 mr-2 py-1 border border-gray-5 rounded w-30 mx-2"
                  value={updatedSettings.payment_wait_xdays_earlier_or_later}
                  onChange={e =>
                    setDirtySettings({
                      ...dirtySettings,
                      payment_wait_xdays_earlier_or_later: e.target.value as AutoCollectWaitOptions
                    })
                  }
                >
                  <option value="earlier">earlier</option>
                  <option value="later">later</option>
                </select>
              </div>
            </div>

            <div className="flex items-center my-2 space-x-2 p-3 bg-amber-100 rounded text-orange-700 font-semibold">
              <InfoIcon className="w-4 h-4" />
              <div>
                The schedule for Auto Collection will be extended by the same number of days as the
                delay in sending the email.
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="items-end w-full border-gray-200 border-t pt-4 pl-2">
        <button
          className={`bg-blue-500  text-white px-4 py-2 rounded ${
            isDirty ? "hover:bg-blue-700" : "cursor-not-allowed bg-gray-400"
          }`}
          disabled={!isDirty}
          onClick={saveAndContinue}
        >
          Save
        </button>
        {isDirty && (
          <button
            className="hover:text-red-600 text-gray-500 px-4 py-2 ml-2"
            onClick={() => setDirtySettings({})}
          >
            Discard
          </button>
        )}
      </footer>
    </>
  );
};

export default ConfigureInvoicePreferenceTab;
